import React from "react";
import {
  QUEUEING_STATUS,
  fingerprintRequest,
} from "../../hooks/useWhatIfQueueing";
import {
  buildQueueingRequest,
  deriveQueueJobs,
  readStaffCount,
  summarizeQueueing,
  utilizationBand,
} from "../../utils/whatif/whatIfQueueing";
import {
  formatCount,
  formatDurationAlt,
  formatDurationShort,
  formatPercent,
} from "../../utils/whatif/whatIfFormat";

// Exported alongside the pieces below: weekly load is always hours-denominated
// regardless of the page's minutes/hours toggle, and the Save Scenario tab's
// read-only table needs to format it identically rather than re-deriving it.
export const HOURS = (value) =>
  Number.isFinite(Number(value))
    ? Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })
    : "-";

// A duration in the page's chosen unit, with the other unit alongside as an
// approx hint — hours by default reads as minutes too, and vice versa.
// Exported: the Save Scenario tab's read-only queueing table reuses this
// (and the pieces below) rather than re-implementing the same formatting.
export const Duration = ({ minutes, timeUnit }) => (
  <>
    {formatDurationShort(minutes, timeUnit)}{" "}
    <span className="muted wif-q-alt-unit">
      (~{formatDurationAlt(minutes, timeUnit)})
    </span>
  </>
);

// The task/role assignments behind a job's service time. Collapsed by default —
// a busy job can hold a dozen, and the column that matters is the total.
export const Assignments = ({ assignments, timeUnit }) => {
  const [open, setOpen] = React.useState(false);

  return (
    <div className="wif-q-assignments">
      <button
        type="button"
        className="wif-link-btn"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {open ? "Hide" : "Show"} {formatCount(assignments.length)} assignment
        {assignments.length === 1 ? "" : "s"}
      </button>

      {open && (
        <ul className="wif-q-assignment-list">
          {assignments.map((item) => (
            <li key={`${item.taskId}-${item.role}`}>
              <span className={`wif-q-role wif-q-role--${item.role}`}>
                {item.role}
              </span>
              <span className="wif-q-assignment-task">{item.taskName}</span>
              <span className="wif-q-assignment-minutes">
                {formatDurationShort(item.minutes, timeUnit)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const Utilization = ({ computed }) => {
  if (!computed) return <span className="wif-q-pending">—</span>;

  const band = utilizationBand(computed.utilizationPercent, computed.stable);
  // The bar is capped at 100% width even when utilization is not, so an
  // overloaded job reads as "full and then some" rather than blowing the column.
  const width = Math.max(0, Math.min(100, computed.utilizationPercent));

  return (
    <div className={`wif-q-util wif-q-util--${band}`}>
      <span className="wif-q-util-value">
        {formatPercent(computed.utilizationPercent)}
        {band === "unstable" && " ⚠"}
      </span>
      <span className="wif-q-util-bar" aria-hidden="true">
        <span className="wif-q-util-fill" style={{ width: `${width}%` }} />
      </span>
    </div>
  );
};

export const QueueWait = ({ computed, timeUnit }) => {
  if (!computed) return <span className="wif-q-pending">—</span>;

  if (utilizationBand(computed.utilizationPercent, computed.stable) === "unstable") {
    return <span className="wif-q-unbounded">unbounded — add staff</span>;
  }

  return computed.queueWaitMinutesPerCase == null ? (
    <span className="wif-q-pending">—</span>
  ) : (
    <Duration minutes={computed.queueWaitMinutesPerCase} timeUnit={timeUnit} />
  );
};

// Exported: the Save Scenario tab renders the same rows read-only (no staff
// input, no `onStaffChange`) so the final review can't drift from the table
// the user actually ran the analysis against.
export const JobRow = ({
  job,
  computed,
  isBottleneck,
  staff,
  timeUnit,
  onStaffChange,
  readOnly = false,
}) => (
  <div
    className={`wif-q-row${isBottleneck ? " wif-q-row--bottleneck" : ""}${
      computed && !computed.stable ? " wif-q-row--unstable" : ""
    }`}
  >
    <div className="wif-q-cell wif-q-cell--job">
      <div className="wif-q-job-name">
        {job.jobName}
        {isBottleneck && (
          <span className="wif-q-bottleneck-badge">queueing bottleneck</span>
        )}
      </div>
      <Assignments assignments={job.assignments} timeUnit={timeUnit} />
    </div>

    <div className="wif-q-cell wif-q-cell--staff">
      <span className="wif-q-cell-label">Staff</span>
      {readOnly ? (
        <span className="wif-q-num">{staff}</span>
      ) : (
        <input
          type="number"
          className="form-input"
          min="1"
          step="1"
          value={staff}
          aria-label={`Staff count for ${job.jobName}`}
          onChange={(event) => onStaffChange(job.jobId, event.target.value)}
        />
      )}
    </div>

    <div className="wif-q-cell wif-q-cell--num">
      <span className="wif-q-cell-label">Service / case</span>
      <span className="wif-q-num">
        <Duration minutes={job.serviceMinutesPerCase} timeUnit={timeUnit} />
      </span>
    </div>

    <div className="wif-q-cell wif-q-cell--num">
      <span className="wif-q-cell-label">Weekly load</span>
      <span className="wif-q-num">
        {computed ? (
          `${HOURS(computed.weeklyWorkloadHours)} h / ${HOURS(computed.weeklyCapacityHours)} h`
        ) : (
          <span className="wif-q-pending">—</span>
        )}
      </span>
    </div>

    <div className="wif-q-cell wif-q-cell--num">
      <span className="wif-q-cell-label">Utilization</span>
      <Utilization computed={computed} />
    </div>

    <div className="wif-q-cell wif-q-cell--num">
      <span className="wif-q-cell-label">Queue wait / case</span>
      <span className="wif-q-num">
        <QueueWait computed={computed} timeUnit={timeUnit} />
      </span>
    </div>
  </div>
);

// The "Queuing Analysis" tab: demand in, staffing per job, and the resulting
// utilization and queue time from the backend.
const QueueingTab = ({ scenario, timeUnit, queueing }) => {
  const jobs = React.useMemo(() => deriveQueueJobs(scenario), [scenario]);
  const summary = React.useMemo(
    () => summarizeQueueing(jobs, queueing.result),
    [jobs, queueing.result],
  );

  // Built once per change of input and reused for both the run and the staleness
  // check, so editing a staff box does not rebuild it twice per keystroke.
  const { arrivalPerWeek, staffByJob } = queueing;
  const request = React.useMemo(
    () =>
      buildQueueingRequest({ arrivalPerWeek, jobs, staffByJob, scenario }),
    [arrivalPerWeek, jobs, staffByJob, scenario],
  );

  const stale = Boolean(
    queueing.result && queueing.ranWith !== fingerprintRequest(request),
  );
  const loading = queueing.status === QUEUEING_STATUS.loading;
  const byJobId = queueing.result?.byJobId || {};

  if (!jobs.length) {
    return (
      <p className="muted">
        No job holds a role on a task that survives this scenario, so there is
        nothing to queue. Assign a job in the Job Allocation tab first.
      </p>
    );
  }

  return (
    <>
      <p className="muted">
        Utilization and queue time for each job at a given demand. A job is one
        resource pool: every role it holds across the process draws on the same
        staff, so its service time per case is the sum of all of them.
      </p>

      <div className="wif-q-controls">
        <div className="wif-q-arrival">
          <label htmlFor="wif-arrival-rate">Cases arriving per week</label>
          <input
            id="wif-arrival-rate"
            type="number"
            className="form-input"
            min="1"
            step="1"
            placeholder="e.g. 40"
            value={queueing.arrivalPerWeek}
            onChange={(event) => queueing.setArrivalPerWeek(event.target.value)}
          />
        </div>

        <button
          type="button"
          className="primary-btn"
          disabled={!queueing.canRun}
          onClick={() => queueing.run(request)}
        >
          {loading ? "Analysing..." : stale ? "Re-run analysis" : "Run analysis"}
        </button>

        {queueing.result && (
          <button
            type="button"
            className="secondary-btn"
            disabled={loading}
            onClick={queueing.reset}
          >
            Clear
          </button>
        )}
      </div>

      <div className="wif-q-facts">
        <span>
          <strong>{formatCount(summary.jobCount)}</strong> job
          {summary.jobCount === 1 ? "" : "s"} staffed
        </span>
        <span>
          <strong>
            {formatDurationShort(summary.serviceMinutesPerCase, timeUnit)}
          </strong>{" "}
          <span className="muted wif-q-alt-unit">
            (~{formatDurationAlt(summary.serviceMinutesPerCase, timeUnit)})
          </span>{" "}
          total service time per case
        </span>
        {queueing.result && (
          <span>
            peak utilization{" "}
            <strong>{formatPercent(summary.peakUtilization)}</strong>
          </span>
        )}
      </div>

      {queueing.status === QUEUEING_STATUS.error && (
        <ul className="whatif-warnings">
          <li>{queueing.error}</li>
        </ul>
      )}

      {stale && queueing.status !== QUEUEING_STATUS.error && (
        <ul className="whatif-warnings">
          <li>
            Inputs have changed since this ran. The figures below still describe
            the previous staffing — re-run to update them.
          </li>
        </ul>
      )}

      {queueing.result && summary.unstableCount > 0 && (
        <ul className="whatif-warnings">
          <li>
            {formatCount(summary.unstableCount)} job
            {summary.unstableCount === 1 ? " is" : "s are"} over 100% utilization:
            more work arrives each week than that staffing can clear, so the queue
            grows without bound. Add staff or move work off them, then re-run.
          </li>
        </ul>
      )}

      <div className={`wif-q-table${stale ? " is-stale" : ""}`}>
        <div className="wif-q-row wif-q-head">
          <div className="wif-q-cell wif-q-cell--job">Job</div>
          <div className="wif-q-cell wif-q-cell--staff">Staff</div>
          <div className="wif-q-cell wif-q-cell--num">Service / case</div>
          <div className="wif-q-cell wif-q-cell--num">Weekly load</div>
          <div className="wif-q-cell wif-q-cell--num">Utilization</div>
          <div className="wif-q-cell wif-q-cell--num">Queue wait / case</div>
        </div>

        {jobs.map((job) => (
          <JobRow
            key={job.key}
            job={job}
            computed={byJobId[String(job.jobId)] || null}
            isBottleneck={
              queueing.result?.bottleneckJobId != null &&
              String(queueing.result.bottleneckJobId) === String(job.jobId)
            }
            staff={readStaffCount(
              queueing.staffByJob,
              job.jobId,
              job.recordedStaffCount,
            )}
            timeUnit={timeUnit}
            onStaffChange={queueing.setStaff}
          />
        ))}
      </div>

      <p className="wif-q-note">
        Staff starts from the headcount recorded against each job. Weekly capacity
        and the queue-time model come from the backend; the service time per case
        is this scenario's own, so eliminating a task or reassigning a role
        changes it here too.
      </p>
    </>
  );
};

export default QueueingTab;
